import { useCallback } from 'react';
import { useSignAndExecuteTransaction, useSignPersonalMessage, useSignTransaction, useSuiClient } from '@mysten/dapp-kit';
import type { SuiSignAndExecuteTransactionOutput } from '@mysten/wallet-standard';
import { Transaction } from '@mysten/sui/transactions';
import { useMutation } from '@tanstack/react-query';
import { SuiGraphQLClient } from '@mysten/sui/graphql';
import { verifyPersonalMessageSignature as defaultVerifyPersonalMessageSignature } from '@mysten/sui/verify';

import { useWalletConnectionResolver } from './useWalletConnectionResolver';
import type { WalletSliceKey } from '../types/wallet-slice.type';
import { APP_CFG } from '@/configs';
import { useCurrentConnection } from './useCurrentConnection';
import { useWalletType } from './useWalletType';
import { resolveWalletSliceKey } from '../utils/wallet.util';
import { recordE2ETransaction } from '@/utils/e2e';

type UseWalletTransactionParams<Result> = {
	chain?: `sui:${string}`;
	execute?: ({ bytes, signature }: { bytes: string; signature: string }) => Promise<Result>;
};

export function useWalletTransaction<Result extends SuiSignAndExecuteTransactionOutput>(params: UseWalletTransactionParams<Result> = {}) {
	const { chain = `sui:${APP_CFG.NETWORK}`, execute } = params;

	const resolveWalletConnection = useWalletConnectionResolver();
	const client = useSuiClient();
	const { account: currentConnectionAccount, authType: currentAuthType } = useCurrentConnection();
	const { mutateAsync: signAndExecuteMutation } = useSignAndExecuteTransaction<Result>({ execute });
	const { mutateAsync: signTransactionMutation } = useSignTransaction();
	const { mutateAsync: signPersonalMessageMutation } = useSignPersonalMessage();
	const { activeWalletType } = useWalletType();

	const resolveTargetWalletType = useCallback(
		(walletType?: WalletSliceKey | null): WalletSliceKey => {
			if (walletType) {
				return walletType;
			}

			if (activeWalletType) {
				return activeWalletType;
			}

			const walletTypeFromAuth = resolveWalletSliceKey(currentAuthType);
			if (walletTypeFromAuth) {
				return walletTypeFromAuth;
			}

			return 'zkLoginWallet';
		},
		[activeWalletType, currentAuthType],
	);

	const restorePreviousConnection = useCallback(
		async (previousWalletType: WalletSliceKey | null, targetWalletType: WalletSliceKey) => {
			if (!previousWalletType || previousWalletType === targetWalletType) {
				return;
			}

			try {
				await resolveWalletConnection(previousWalletType);
			} catch (error) {
				console.warn('Failed to restore previous wallet connection', error);
			}
		},
		[resolveWalletConnection],
	);

	const signTransaction = useCallback(
		async (transaction: Transaction, walletType?: WalletSliceKey) => {
			const previousWalletType = activeWalletType;
			const targetWalletType = resolveTargetWalletType(walletType ?? previousWalletType ?? null);
			const { account } = await resolveWalletConnection(targetWalletType);
			try {
				return await signTransactionMutation({
					transaction,
					account,
					chain,
				});
			} finally {
				await restorePreviousConnection(previousWalletType, targetWalletType);
			}
		},
		[activeWalletType, chain, resolveTargetWalletType, resolveWalletConnection, restorePreviousConnection, signTransactionMutation],
	);

	const signPersonalMessage = useCallback(
		async (message: string | Uint8Array, walletType?: WalletSliceKey): Promise<string> => {
			const previousWalletType = activeWalletType;
			const targetWalletType = resolveTargetWalletType(walletType ?? previousWalletType ?? null);
			const { account } = await resolveWalletConnection(targetWalletType);
			const messageBytes = typeof message === 'string' ? new TextEncoder().encode(message) : message;

			try {
				const { signature } = await signPersonalMessageMutation({
					message: messageBytes,
					account,
					chain,
				});
				return signature;
			} finally {
				await restorePreviousConnection(previousWalletType, targetWalletType);
			}
		},
		[activeWalletType, chain, resolveTargetWalletType, resolveWalletConnection, restorePreviousConnection, signPersonalMessageMutation],
	);

	const signAndExecuteTransaction = useCallback(
		async (transaction: Transaction, walletType?: WalletSliceKey): Promise<Result> => {
			const previousWalletType = activeWalletType;
			const targetWalletType = resolveTargetWalletType(walletType ?? previousWalletType ?? null);
			const { account } = await resolveWalletConnection(targetWalletType);

			try {
				const result = await signAndExecuteMutation({
					transaction,
					account,
					chain,
				});
				if (result && typeof (result as any).digest === 'string') {
					recordE2ETransaction({ digest: (result as any).digest, source: 'signAndExecuteTransaction' });
				}
				return result;
			} catch (error) {
				console.error('Transaction signing failed:', error);
				throw error;
			} finally {
				await restorePreviousConnection(previousWalletType, targetWalletType);
			}
		},
		[activeWalletType, chain, resolveTargetWalletType, resolveWalletConnection, restorePreviousConnection, signAndExecuteMutation],
	);

	const executeTransaction = useMutation({
		mutationFn: async ({
			bytes,
			signature,
			reportTransactionEffects,
		}: {
			bytes: string;
			signature: string;
			reportTransactionEffects?: (effects?: string | number[]) => void;
		}) => {
			const result = await client.executeTransactionBlock({
				transactionBlock: bytes,
				signature: signature,
				options: {
					showEvents: true,
					showEffects: true,
					showRawEffects: true,
				},
			});

			if (result && typeof (result as any).digest === 'string') {
				recordE2ETransaction({ digest: (result as any).digest, source: 'executeTransaction' });
			}

			reportTransactionEffects?.(result.rawEffects);

			return result;
		},
		onError: (error) => {
			throw error;
		},
	});

	const verifyPersonalMessageSignature = useCallback(
		async (message: string, signature: string) => {
			const account = currentConnectionAccount;
			if (!account) {
				throw new Error('No account available');
			}

			return defaultVerifyPersonalMessageSignature(new TextEncoder().encode(message), signature, {
				client: new SuiGraphQLClient({
					url: `https://graphql.${APP_CFG.NETWORK}.sui.io/graphql`,
					network: APP_CFG.NETWORK,
				}),
				address: account.address,
			});
		},
		[currentConnectionAccount],
	);

	return { signTransaction, signPersonalMessage, signAndExecuteTransaction, executeTransaction, verifyPersonalMessageSignature };
}
